import * as THREE from "three";

/** Sample face centroids for ICP. */
export function sampleSurfacePoints(
  geometry: THREE.BufferGeometry,
  count: number,
  matrix?: THREE.Matrix4
): THREE.Vector3[] {
  const pos = geometry.getAttribute("position") as THREE.BufferAttribute;
  const index = geometry.index;
  const faceCount = index ? index.count / 3 : pos.count / 3;
  if (faceCount < 1) return [];

  const step = Math.max(1, Math.floor(faceCount / count));
  const points: THREE.Vector3[] = [];
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const mid = new THREE.Vector3();

  for (let f = 0; f < faceCount && points.length < count; f += step) {
    let ia: number;
    let ib: number;
    let ic: number;
    if (index) {
      ia = index.getX(f * 3);
      ib = index.getX(f * 3 + 1);
      ic = index.getX(f * 3 + 2);
    } else {
      ia = f * 3;
      ib = f * 3 + 1;
      ic = f * 3 + 2;
    }
    a.fromBufferAttribute(pos, ia);
    b.fromBufferAttribute(pos, ib);
    c.fromBufferAttribute(pos, ic);
    mid.copy(a).add(b).add(c).multiplyScalar(1 / 3);
    if (matrix) mid.applyMatrix4(matrix);
    points.push(mid.clone());
  }
  return points;
}

function col(m: THREE.Matrix3, c: number): THREE.Vector3 {
  const e = m.elements;
  return new THREE.Vector3(e[c * 3], e[c * 3 + 1], e[c * 3 + 2]);
}

function setCol(m: THREE.Matrix3, c: number, v: THREE.Vector3) {
  const e = m.elements;
  e[c * 3] = v.x;
  e[c * 3 + 1] = v.y;
  e[c * 3 + 2] = v.z;
}

function det3(m: THREE.Matrix3): number {
  const e = m.elements;
  return (
    e[0] * (e[4] * e[8] - e[5] * e[7]) -
    e[3] * (e[1] * e[8] - e[2] * e[7]) +
    e[6] * (e[1] * e[5] - e[2] * e[4])
  );
}

/** Jacobi eigen-decomposition of symmetric 3×3 → V columns = eigenvectors, d = eigenvalues. */
function jacobiEigenSymmetric(A: THREE.Matrix3): { V: THREE.Matrix3; d: number[] } {
  const a = A.elements.slice();
  const d = [a[0], a[4], a[8]];
  const V = new THREE.Matrix3().identity();

  const offDiag = (i: number, j: number) => a[i + j * 3];
  const setSym = (i: number, j: number, v: number) => {
    a[i + j * 3] = v;
    a[j + i * 3] = v;
  };

  for (let iter = 0; iter < 40; iter++) {
    let p = 0;
    let q = 1;
    let max = Math.abs(offDiag(0, 1));
    for (const [i, j] of [
      [0, 2],
      [1, 2],
    ] as [number, number][]) {
      const v = Math.abs(offDiag(i, j));
      if (v > max) {
        max = v;
        p = i;
        q = j;
      }
    }
    if (max < 1e-14) break;

    const app = d[p];
    const aqq = d[q];
    const apq = offDiag(p, q);
    const phi = 0.5 * Math.atan2(2 * apq, aqq - app);
    const c = Math.cos(phi);
    const s = Math.sin(phi);

    d[p] = c * c * app - 2 * s * c * apq + s * s * aqq;
    d[q] = s * s * app + 2 * s * c * apq + c * c * aqq;
    setSym(p, q, 0);

    for (let k = 0; k < 3; k++) {
      if (k === p || k === q) continue;
      const aik = offDiag(k, p);
      const aikq = offDiag(k, q);
      setSym(k, p, c * aik - s * aikq);
      setSym(k, q, s * aik + c * aikq);
    }

    for (let r = 0; r < 3; r++) {
      const vip = V.elements[p * 3 + r];
      const viq = V.elements[q * 3 + r];
      // V is column-major: column p has indices p*3, p*3+1, p*3+2
      V.elements[p * 3 + r] = c * vip - s * viq;
      V.elements[q * 3 + r] = s * vip + c * viq;
    }
  }

  return { V, d };
}

/**
 * Rigid transform mapping source points → target points (Kabsch, no scale).
 * Returns a Matrix4 applied as p' = M * p.
 */
export function computeRigidTransform(
  source: THREE.Vector3[],
  target: THREE.Vector3[]
): THREE.Matrix4 {
  const n = Math.min(source.length, target.length);
  if (n < 3) return new THREE.Matrix4().identity();

  const cs = new THREE.Vector3();
  const ct = new THREE.Vector3();
  for (let i = 0; i < n; i++) {
    cs.add(source[i]);
    ct.add(target[i]);
  }
  cs.multiplyScalar(1 / n);
  ct.multiplyScalar(1 / n);

  // Covariance H = Σ (si - cs) (ti - ct)^T   → column-major
  const H = new THREE.Matrix3().set(0, 0, 0, 0, 0, 0, 0, 0, 0);
  const he = H.elements;
  for (let i = 0; i < n; i++) {
    const sx = source[i].x - cs.x;
    const sy = source[i].y - cs.y;
    const sz = source[i].z - cs.z;
    const tx = target[i].x - ct.x;
    const ty = target[i].y - ct.y;
    const tz = target[i].z - ct.z;
    // H_ij += s_i * t_j  → column j, row i → index i + j*3
    he[0] += sx * tx;
    he[1] += sy * tx;
    he[2] += sz * tx;
    he[3] += sx * ty;
    he[4] += sy * ty;
    he[5] += sz * ty;
    he[6] += sx * tz;
    he[7] += sy * tz;
    he[8] += sz * tz;
  }

  // SVD via eigen of H^T H: V from eigen(H^T H), U = H V S^{-1}
  const Ht = H.clone().transpose();
  const HtH = new THREE.Matrix3().multiplyMatrices(Ht, H);
  const { V, d } = jacobiEigenSymmetric(HtH);

  // Sort eigenvalues descending
  const order = [0, 1, 2].sort((i, j) => d[j] - d[i]);
  const Vsorted = new THREE.Matrix3();
  const S = [0, 0, 0];
  for (let k = 0; k < 3; k++) {
    setCol(Vsorted, k, col(V, order[k]));
    S[k] = Math.sqrt(Math.max(0, d[order[k]]));
  }

  const U = new THREE.Matrix3();
  for (let k = 0; k < 3; k++) {
    const vk = col(Vsorted, k);
    const uk = vk.clone().applyMatrix3(H);
    if (S[k] > 1e-10) uk.multiplyScalar(1 / S[k]);
    else uk.set(0, 0, 0);
    setCol(U, k, uk);
  }

  // Orthonormalize U
  let u0 = col(U, 0);
  if (u0.lengthSq() < 1e-12) u0.set(1, 0, 0);
  else u0.normalize();
  setCol(U, 0, u0);

  let u1 = col(U, 1);
  u1.addScaledVector(u0, -u1.dot(u0));
  if (u1.lengthSq() < 1e-12) {
    u1 = new THREE.Vector3(0, 1, 0).cross(u0);
    if (u1.lengthSq() < 1e-12) u1.set(0, 0, 1).cross(u0);
  }
  u1.normalize();
  setCol(U, 1, u1);
  setCol(U, 2, new THREE.Vector3().crossVectors(u0, u1).normalize());

  // Ensure V is right-handed too
  const v0 = col(Vsorted, 0);
  const v1 = col(Vsorted, 1);
  setCol(Vsorted, 2, new THREE.Vector3().crossVectors(v0, v1).normalize());

  // R = V U^T
  const R = new THREE.Matrix3().multiplyMatrices(Vsorted, U.clone().transpose());
  if (det3(R) < 0) {
    const v2 = col(Vsorted, 2).multiplyScalar(-1);
    setCol(Vsorted, 2, v2);
    R.multiplyMatrices(Vsorted, U.clone().transpose());
  }

  const rotatedCs = cs.clone().applyMatrix3(R);
  const t = ct.clone().sub(rotatedCs);

  const m = new THREE.Matrix4();
  // Row-major args to set():
  m.set(
    R.elements[0],
    R.elements[3],
    R.elements[6],
    t.x,
    R.elements[1],
    R.elements[4],
    R.elements[7],
    t.y,
    R.elements[2],
    R.elements[5],
    R.elements[8],
    t.z,
    0,
    0,
    0,
    1
  );
  return m;
}

export function applyMatrixToGeometry(
  geometry: THREE.BufferGeometry,
  matrix: THREE.Matrix4
) {
  geometry.applyMatrix4(matrix);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}

/** Faster path when normals already exist — applyMatrix4 transforms them. */
export function applyMatrixToGeometryLite(
  geometry: THREE.BufferGeometry,
  matrix: THREE.Matrix4
) {
  geometry.applyMatrix4(matrix);
  if (!geometry.getAttribute("normal")) {
    geometry.computeVertexNormals();
  }
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}

/** Manual rigid offset applied on top of registered/base geometry. */
export interface JawTransform {
  tx: number;
  ty: number;
  tz: number;
  rx: number;
  ry: number;
  rz: number;
}

export const IDENTITY_JAW: JawTransform = {
  tx: 0,
  ty: 0,
  tz: 0,
  rx: 0,
  ry: 0,
  rz: 0,
};

export function jawTransformToMatrix(t: JawTransform): THREE.Matrix4 {
  return jawTransformToMatrixInto(t, new THREE.Matrix4());
}

const jawComposeScratch = {
  pos: new THREE.Vector3(),
  eul: new THREE.Euler(),
  quat: new THREE.Quaternion(),
  scale: new THREE.Vector3(1, 1, 1),
};

/** Write jaw transform into an existing matrix (avoids alloc in hot loops). */
export function jawTransformToMatrixInto(
  t: JawTransform,
  target: THREE.Matrix4
): THREE.Matrix4 {
  jawComposeScratch.pos.set(t.tx, t.ty, t.tz);
  jawComposeScratch.eul.set(
    THREE.MathUtils.degToRad(t.rx),
    THREE.MathUtils.degToRad(t.ry),
    THREE.MathUtils.degToRad(t.rz),
    "XYZ"
  );
  jawComposeScratch.quat.setFromEuler(jawComposeScratch.eul);
  return target.compose(
    jawComposeScratch.pos,
    jawComposeScratch.quat,
    jawComposeScratch.scale
  );
}

export function matrixToJawTransform(m: THREE.Matrix4): JawTransform {
  const pos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  m.decompose(pos, quat, scale);
  const eul = new THREE.Euler().setFromQuaternion(quat, "XYZ");
  return {
    tx: pos.x,
    ty: pos.y,
    tz: pos.z,
    rx: THREE.MathUtils.radToDeg(eul.x),
    ry: THREE.MathUtils.radToDeg(eul.y),
    rz: THREE.MathUtils.radToDeg(eul.z),
  };
}

/**
 * Group offset that carries geometry baked at pose `from` to live pose `to`
 * (delta · from = to, so delta = to · from⁻¹).
 */
export function deltaJawTransform(from: JawTransform, to: JawTransform): JawTransform {
  const mFrom = jawTransformToMatrix(from);
  const mTo = jawTransformToMatrix(to);
  const mDelta = new THREE.Matrix4().multiplyMatrices(mTo, mFrom.clone().invert());
  return matrixToJawTransform(mDelta);
}
